import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { Layout } from "@/components/Layout";
import { ratesQueryOptions } from "@/components/CountryPage";
import { SITE_URL } from "@/lib/seo";
import { withTodayDate } from "@/lib/dateIntent";
import { buildToolSchemas } from "@/lib/tools-schema";

const PurityConverter = lazy(() =>
  import("@/components/GoldToolsSuite").then((m) => ({ default: m.PurityConverter })),
);

export const Route = createFileRoute("/tools/gold-purity-converter")({
  head: () => ({
    meta: [
      { title: withTodayDate("Gold Purity Converter — 24K to 22K, 21K, 18K & 14K") },
      {
        name: "description",
        content:
          "Free gold purity converter. Instantly convert any gold weight (grams, tola, ounce) across 24K, 22K, 21K, 18K and 14K using live 2026 international spot rates for Pakistan (PKR) and India (INR).",
      },
      { property: "og:title", content: "Gold Purity & Weight Converter — Live Rates" },
      { property: "og:description", content: "Convert gold weight and purity instantly with live 24K base rates." },
      { property: "og:url", content: `${SITE_URL}/tools/gold-purity-converter` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/tools/gold-purity-converter` }],
    scripts: buildToolSchemas({
      name: "Gold Purity Converter",
      description: "Convert any gold weight across 24K, 22K, 21K, 18K and 14K using live spot rates.",
      path: "/tools/gold-purity-converter",
      steps: [
        { name: "Enter weight", text: "Enter the gold weight in grams, tola or ounces." },
        { name: "Pick karat", text: "Select the current karat (24K, 22K, 21K, 18K or 14K)." },
        { name: "Read converted value", text: "Instantly see the equivalent weight and live price in every other karat." },
      ],
    }),
  }),
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(ratesQueryOptions);
  },
  component: Page,
});

function Page() {
  return (
    <Layout>
      <section className="mx-auto max-w-6xl px-4 pt-10 pb-2">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/tools" className="hover:text-gold">Tools</Link> / Gold Purity Converter
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">Gold Purity &amp; Weight Converter</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Enter any weight in grams, tola or ounce and instantly see its value across every common karat
          (24K, 22K, 21K, 18K, 14K) using live international spot rates and today's USD/PKR or USD/INR.
        </p>
      </section>
      <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>}>
        <PurityConverter />
      </Suspense>
    </Layout>
  );
}